import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cta, CTA_STYLES, CTA_COLORS } from "@/lib/cta";
import { BRAND } from "@/lib/brand";
import heroBackground from "@/assets/hero-background.jpg";
import { Users, FileCheck, Shield, DollarSign, Briefcase, Scale, ChevronDown, ChevronUp, UserCheck, Handshake, Target, Workflow } from "lucide-react";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

/**
 * Brokers (Broker‑Sellers) Landing Page (FR/EN) – Standalone
 * - Header/Footer autonomous
 * - EN default language
 * - Uses BRAND palette from e-learning
 * - CTAs: /auth?role=broker and /questionnaire
 */

const THEME = BRAND;




function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left"
      >
        <span className="font-medium" style={{ color: THEME.navy }}>{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5" style={{ color: THEME.goldEnd }} />
        ) : (
          <ChevronDown className="w-5 h-5" style={{ color: THEME.goldEnd }} />
        )}
      </button>
      {isOpen && (
        <p className="pb-4 text-sm" style={{ color: THEME.graphite }}>
          {answer}
        </p>
      )}
    </div>
  );
}

// ===== Copy (EN default, FR included) =====
const copy = {
  en: {
    hero: {
      eyebrow: "BROKER‑SELLERS",
      title: "Accelerate your transactions. Multiply qualified buyers.",
      subtitle: "Plug into our buyer network and turn your sales process into an unprecedented success.",
      ctaPrimary: "Start your listing",
      ctaSecondary: "Intro call",
    },
    value: {
      title: "Your Strategic Partner for Accelerated Sales",
      lead: "Maximize your efficiency with intelligent buyer-seller matching, dormant file reactivation, and access to pre-qualified, financing-ready buyers.",
    },
    valueCards: [
      { 
        h: "Access to qualified buyers", 
        p: "Verified, active buyers ready to review quickly.",
        hasIcon: true
      },
      { 
        h: "Pre‑financing workflows", 
        p: "Lender‑friendly packaging reduces back‑and‑forth.",
        hasIcon: false
      },
      { 
        h: "Transparent management fees", 
        p: "Management fee of 1% of the broker's commission.",
        hasIcon: true
      },
    ],
    financing: {
      title: "Traditional or non-traditional financing opportunity",
      blurb: "All businesses accepted and published on the platform are submitted to our lending partners",
    },
    confidentiality: {
      title: "Absolute confidentiality",
      items: [
        "Anonymized listings to protect client identity.",
        "Your clients' information remains protected at every stage until the contact request.",
      ],
    },
    how: {
      title: "How it works",
      steps: ["Initial evaluation", "File preparation", "Go‑to‑market", "Financing", "Closing"],
      docs: "NDA, LOI and other documents are included on‑platform.",
      advisors: "Free access to a directory of consultants across all specialties.",
    },
    successTeam: {
      title: "Your Success Team: Consultants & Experts",
      subtitle: "To strengthen buyer credibility and secure transactions, AcquizGroup provides you with two specialized networks, accessible directly from your dashboard.",
      consultant: {
        title: "1. The Consultant Program: Strengthen Buyer Profiles",
        desc: "Help your buyers stand out by integrating cutting-edge expertise that enhances their credibility with sellers and lenders.",
        items: [
          {
            h: "Strategic Enhancement",
            p: "Recommend advisors or business partners with industry expertise, licenses, or key skills to strengthen buyer profiles."
          },
          {
            h: "Business Partnership",
            p: "Identify strategic allies to integrate directly into deal structures and solidify acquisition proposals."
          }
        ]
      },
      experts: {
        title: "2. The Experts Program: Secure the Transaction",
        desc: "Ensure your deals cross the finish line in full compliance with direct access to essential specialists.",
        items: [
          {
            h: "Due Diligence & Closing",
            p: "Collaborate with our in-house experts (Business and Immigration Lawyers, Notaries, CPAs, and Tax Advisors)."
          },
          {
            h: "Risk Management",
            p: "Rigorous technical support to ensure every legal and tax aspect of acquisitions is optimized."
          }
        ]
      }
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          q: "What are the management fees for brokers?",
          a: "AcquizGroup charges a transparent management fee of 1% of the broker's commission. No hidden fees, no surprises."
        },
        {
          q: "How does the buyer matching process work?",
          a: "Our intelligent matching algorithm connects your listings with pre-qualified, financing-ready buyers based on industry, budget, and acquisition criteria."
        },
        {
          q: "Can I reactivate dormant client files?",
          a: "Yes! Our platform helps you identify and reconnect with past clients who may be ready to re-enter the market with fresh opportunities."
        },
        {
          q: "How is client confidentiality protected?",
          a: "All listings are anonymized until NDA execution. Client information remains protected at every stage until a formal contact request is made."
        },
        {
          q: "What financing options are available for buyers?",
          a: "All accepted listings are automatically submitted to our lending partner network, including traditional and non-traditional financing options."
        },
        {
          q: "How do I access the consultant and expert directories?",
          a: "Once registered, you'll have free access to our comprehensive directory of consultants and experts directly from your broker dashboard."
        }
      ]
    },
    cta: {
      title: "Ready to grow your pipeline?",
      subtitle: "Join our network and engage buyers with lender-ready files.",
      primary: "Start your broker dossier",
      secondary: "Contact the team",
    },
  },
  fr: {
    hero: {
      eyebrow: "COURTIERS‑VENDEURS",
      title: "Accélérez vos transactions. Multipliez vos acheteurs qualifiés.",
      subtitle:
        "Intégrez notre réseau d'acheteurs sérieux et faites de votre processus de vente un succès sans précédent.",
      ctaPrimary: "Démarrer votre dossier de courtier",
      ctaSecondary: "Appel d'introduction",
    },
    value: {
      title: "Votre partenaire stratégique pour des ventes accélérées",
      lead: "Maximisez votre efficacité avec un matching intelligent acheteur-vendeur, la réactivation de dossiers dormants et l'accès à des acheteurs pré-qualifiés, prêts pour le financement.",
    },
    valueCards: [
      {
        h: "Accès à un bassin d'acheteurs qualifiés",
        p: "Acheteurs vérifiés et actifs, prêts à analyser rapidement.",
        hasIcon: true
      },
      { 
        h: "Workflows de pré‑financement", 
        p: "Packaging favorable aux prêteurs pour réduire les aller‑retours.",
        hasIcon: false
      },
      { 
        h: "Frais de gestion clairs", 
        p: "Frais de gestion de 1 % de la commission du courtier.",
        hasIcon: true
      },
    ],
    financing: {
      title: "Opportunité de financement traditionnel ou non-traditionnel",
      blurb: "Toutes les entreprises acceptées et publiées sur la plateforme sont soumises à nos partenaires prêteurs",
    },
    confidentiality: {
      title: "Confidentialité absolue",
      items: [
        "Listing anonymisé pour préserver l'identité des clients.",
        "Les informations de vos clients restent protégées à chaque étape jusqu'à la demande de la prise de contact.",
      ],
    },
    how: {
      title: "Comment ça fonctionne",
      steps: ["Évaluation initiale", "Préparation du dossier", "Mise en marché", "Financement", "Closing"],
      docs: "NDA, LOI et autres documents sont inclus sur la plateforme.",
      advisors: "Accès gratuit à un répertoire de consultants de toutes spécialités.",
    },
    successTeam: {
      title: "Votre équipe de réussite : Consultants et Experts",
      subtitle: "Pour renforcer la crédibilité des acheteurs et sécuriser les transactions, AcquizGroup met à votre disposition deux réseaux spécialisés, accessibles directement depuis votre tableau de bord.",
      consultant: {
        title: "1. Le Programme Consultant : Renforcez les profils d'acheteurs",
        desc: "Aidez vos acheteurs à se démarquer en intégrant une expertise de pointe qui renforce leur crédibilité auprès des vendeurs et des prêteurs.",
        items: [
          {
            h: "Renforcement stratégique",
            p: "Recommandez des conseillers ou partenaires commerciaux avec l'expertise sectorielle, les licences ou les compétences clés pour renforcer les profils d'acheteurs."
          },
          {
            h: "Partenariat d'affaires",
            p: "Identifiez des alliés stratégiques à intégrer directement dans les structures de transactions pour solidifier les propositions d'acquisition."
          }
        ]
      },
      experts: {
        title: "2. Le Programme Experts : Sécurisez la transaction",
        desc: "Assurez-vous que vos transactions franchissent la ligne d'arrivée en toute conformité avec un accès direct aux spécialistes essentiels.",
        items: [
          {
            h: "Due Diligence & Closing",
            p: "Collaborez avec nos experts internes (Avocats d'affaires et d'immigration, Notaires, CPA et Conseillers fiscaux)."
          },
          {
            h: "Gestion des risques",
            p: "Support technique rigoureux pour garantir que chaque aspect juridique et fiscal des acquisitions est optimisé."
          }
        ]
      }
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        {
          q: "Quels sont les frais de gestion pour les courtiers ?",
          a: "AcquizGroup facture des frais de gestion transparents de 1% de la commission du courtier. Pas de frais cachés, pas de surprises."
        },
        {
          q: "Comment fonctionne le processus de matching acheteur ?",
          a: "Notre algorithme de matching intelligent connecte vos listings avec des acheteurs pré-qualifiés et prêts pour le financement, basé sur l'industrie, le budget et les critères d'acquisition."
        },
        {
          q: "Puis-je réactiver des dossiers clients dormants ?",
          a: "Oui ! Notre plateforme vous aide à identifier et reconnecter avec d'anciens clients qui pourraient être prêts à revenir sur le marché avec de nouvelles opportunités."
        },
        {
          q: "Comment la confidentialité des clients est-elle protégée ?",
          a: "Tous les listings sont anonymisés jusqu'à l'exécution du NDA. Les informations des clients restent protégées à chaque étape jusqu'à une demande de contact formelle."
        },
        {
          q: "Quelles options de financement sont disponibles pour les acheteurs ?",
          a: "Tous les listings acceptés sont automatiquement soumis à notre réseau de partenaires prêteurs, incluant des options de financement traditionnelles et non-traditionnelles."
        },
        {
          q: "Comment accéder aux répertoires de consultants et d'experts ?",
          a: "Une fois inscrit, vous aurez un accès gratuit à notre répertoire complet de consultants et d'experts directement depuis votre tableau de bord courtier."
        }
      ]
    },
    cta: {
      title: "Prêt à développer votre pipeline ?",
      subtitle: "Rejoignez notre réseau et activez des acheteurs avec des dossiers prêts pour les prêteurs.",
      primary: "Démarrer votre dossier de courtier",
      secondary: "Contacter l'équipe",
    },
  },
} as const;

export default function BrokerSellerPage() {
  const [lang, setLang] = useState<"en" | "fr">("en");
  const t = useMemo(() => copy[lang], [lang]);
  const ctaTexts = cta("broker", lang);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <SiteHeader lang={lang} onToggleLang={() => setLang(lang === "en" ? "fr" : "en")} />

      {/* Hero */}
      <section
        className="w-full relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${THEME.navy}, ${THEME.navyDark})` }}
      >
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(180deg, ${THEME.navy}CC, ${THEME.navyDark}CC)` }}
        ></div>
        <div className="relative mx-auto max-w-7xl px-4 py-10">
          <p className="text-sm uppercase" style={{ color: THEME.silver }}>
            {t.hero.eyebrow}
          </p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-white">{t.hero.title}</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg text-white/85">{t.hero.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/register/brokers"
              className={CTA_STYLES.primary}
              style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}
            >
              {ctaTexts.primary}
            </Link>
            <Link
              to="/contact"
              aria-label={ctaTexts.aria}
              className={`${CTA_STYLES.secondary} border-white/30 text-white`}
            >
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Value props - Centered */}
      <section className="mx-auto max-w-7xl px-4 pt-20 pb-16 text-center">
        <h2 className="text-3xl md:text-4xl font-bold" style={{ color: THEME.navy }}>
          {t.value.title}
        </h2>
        <p className="mt-4 text-lg md:text-xl max-w-3xl mx-auto" style={{ color: THEME.graphite }}>
          {t.value.lead}
        </p>
      </section>

      {/* Value Cards */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <div className="grid md:grid-cols-3 gap-6">
          {t.valueCards.map((c, i) => (
            <div 
              key={i} 
              className="rounded-2xl border p-6 shadow-sm bg-gradient-to-br from-white to-blue-50/50"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${THEME.goldEnd}15` }}
              >
                {i === 0 && <Users className="w-6 h-6" style={{ color: THEME.goldEnd }} />}
                {i === 1 && <Workflow className="w-6 h-6" style={{ color: THEME.goldEnd }} />}
                {i === 2 && <DollarSign className="w-6 h-6" style={{ color: THEME.goldEnd }} />}
              </div>
              <h3 className="font-semibold" style={{ color: THEME.navy }}>
                {c.h}
              </h3>
              <p className="mt-1 text-sm" style={{ color: THEME.graphite }}>
                {c.p}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Financing & Confidentiality */}
      <section className="mx-auto max-w-7xl px-4 pb-12 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border bg-gradient-to-br from-white to-amber-50/30 p-6 shadow-sm">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{ backgroundColor: `${THEME.goldEnd}15` }}
          >
            <Briefcase className="w-6 h-6" style={{ color: THEME.goldEnd }} />
          </div>
          <h3 className="font-semibold" style={{ color: THEME.navy }}>
            {t.financing.title}
          </h3>
          <p className="mt-1 text-sm" style={{ color: THEME.graphite }}>
            {t.financing.blurb}
          </p>
        </div>
        <div className="rounded-2xl border bg-gradient-to-br from-white to-slate-50/50 p-6 shadow-sm">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{ backgroundColor: `${THEME.navy}15` }}
          >
            <Shield className="w-6 h-6" style={{ color: THEME.navy }} />
          </div>
          <h3 className="font-semibold" style={{ color: THEME.navy }}>
            {t.confidentiality.title}
          </h3>
          <ul className="mt-2 list-disc pl-5 text-sm" style={{ color: THEME.graphite }}>
            {t.confidentiality.items.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works + Docs & Consultants */}
      <section className="mx-auto max-w-7xl px-4 pt-8 pb-16">
        <h2 className="text-xl font-semibold text-center mb-8" style={{ color: THEME.navy }}>
          {t.how.title}
        </h2>
        <div className="mt-4 grid md:grid-cols-5 gap-4">
          {t.how.steps.map((s, i) => (
            <div 
              key={i} 
              className={`rounded-xl border p-4 text-sm text-center shadow-sm ${
                i % 2 === 0 ? 'bg-gradient-to-br from-white to-blue-50/30' : 'bg-white'
              }`}
            >
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm"
                style={{ backgroundColor: THEME.navy }}
              >
                {i + 1}
              </div>
              {s}
            </div>
          ))}
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{ backgroundColor: `${THEME.goldEnd}15` }}
            >
              <FileCheck className="w-5 h-5" style={{ color: THEME.goldEnd }} />
            </div>
            <h3 className="font-semibold" style={{ color: THEME.navy }}>
              {lang === "fr" ? "Documents inclus" : "Included documents"}
            </h3>
            <p className="mt-1 text-sm" style={{ color: THEME.graphite }}>
              {t.how.docs}
            </p>
          </div>
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{ backgroundColor: `${THEME.navy}15` }}
            >
              <Users className="w-5 h-5" style={{ color: THEME.navy }} />
            </div>
            <h3 className="font-semibold" style={{ color: THEME.navy }}>
              {lang === "fr" ? "Répertoire des consultants (gratuit)" : "Consultants directory (free)"}
            </h3>
            <p className="mt-1 text-sm" style={{ color: THEME.graphite }}>
              {t.how.advisors}
            </p>
          </div>
        </div>

        {/* Learn More Button */}
        <div className="mt-10 text-center">
          <Link
            to="/general-landing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-lg font-sans"
            style={{
              background: `linear-gradient(90deg, ${THEME.goldStart}, ${THEME.goldEnd})`,
              color: THEME.navy,
            }}
          >
            {lang === "fr" ? "En savoir plus" : "Learn more"}
          </Link>
        </div>
      </section>

      {/* Success Team Section */}
      <section className="mx-auto max-w-7xl px-4 pb-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: THEME.navy }}>
            {t.successTeam.title}
          </h2>
          <p className="mt-4 text-base max-w-3xl mx-auto" style={{ color: THEME.graphite }}>
            {t.successTeam.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Consultant Program */}
          <div className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-8 shadow-sm">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${THEME.goldEnd}20` }}
            >
              <UserCheck className="w-7 h-7" style={{ color: THEME.goldEnd }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: THEME.navy }}>
              {t.successTeam.consultant.title}
            </h3>
            <p className="text-sm mb-6" style={{ color: THEME.graphite }}>
              {t.successTeam.consultant.desc}
            </p>
            <div className="space-y-4">
              {t.successTeam.consultant.items.map((item, i) => (
                <div key={i} className="flex gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${THEME.goldEnd}15` }}
                  >
                    {i === 0 ? (
                      <Target className="w-4 h-4" style={{ color: THEME.goldEnd }} />
                    ) : (
                      <Handshake className="w-4 h-4" style={{ color: THEME.goldEnd }} />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm" style={{ color: THEME.navy }}>{item.h}</h4>
                    <p className="text-sm mt-1" style={{ color: THEME.graphite }}>{item.p}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experts Program */}
          <div className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-8 shadow-sm">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${THEME.navy}15` }}
            >
              <Scale className="w-7 h-7" style={{ color: THEME.navy }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: THEME.navy }}>
              {t.successTeam.experts.title}
            </h3>
            <p className="text-sm mb-6" style={{ color: THEME.graphite }}>
              {t.successTeam.experts.desc}
            </p>
            <div className="space-y-4">
              {t.successTeam.experts.items.map((item, i) => (
                <div key={i} className="flex gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${THEME.navy}15` }}
                  >
                    {i === 0 ? (
                      <FileCheck className="w-4 h-4" style={{ color: THEME.navy }} />
                    ) : (
                      <Shield className="w-4 h-4" style={{ color: THEME.navy }} />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm" style={{ color: THEME.navy }}>{item.h}</h4>
                    <p className="text-sm mt-1" style={{ color: THEME.graphite }}>{item.p}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-4xl px-4 pb-16">
        <h2 className="text-2xl font-bold text-center mb-8" style={{ color: THEME.navy }}>
          {t.faq.title}
        </h2>
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          {t.faq.items.map((item, i) => (
            <FAQItem key={i} question={item.q} answer={item.a} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24">
        <div
          className="rounded-2xl p-8 text-center shadow-lg"
          style={{ background: `linear-gradient(180deg, ${THEME.navyDark}, ${THEME.navy})` }}
        >
          <h2 className="text-2xl font-semibold text-white">{t.cta.title}</h2>
          <p className="mt-2 text-sm" style={{ color: THEME.silver }}>
            {t.cta.subtitle}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/sellerplan"
              className="rounded-2xl border-2 px-5 py-2 text-sm font-semibold transition-all hover:shadow-lg"
              style={{
                borderColor: THEME.gold,
                color: THEME.gold,
              }}
            >
              {lang === "fr" ? "Plan Courtier‑Vendeur" : "Broker‑Seller Plan"}
            </Link>
            <Link
              to="/register/brokers"
              className={CTA_STYLES.primary}
              style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}
            >
              {ctaTexts.primary}
            </Link>
            <Link
              to="/contact"
              aria-label={ctaTexts.aria}
              className={`${CTA_STYLES.secondary} border-white/30 text-white`}
            >
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
}
